import type { ReactNode } from "react";
import { useEffect, useId, useState } from "react";
import styles from "./GetReadyStepSection.module.css";
import { JourneyProgressCircle } from "./JourneyProgressCircle";

export interface GetReadySubStepSectionProps {
	subStepTitle: string;
	completedCount: number;
	totalCount: number;
	defaultOpen?: boolean;
	/**
	 * When set to true, forces the section open (e.g. deep-link focus).
	 * Edge-triggered: once true, opens the section; subsequent renders with
	 * false do not close it so the user can collapse manually.
	 */
	forceOpen?: boolean;
	expandLabel: string;
	collapseLabel: string;
	/** Localized aria-label for the progress circle (e.g. "3 of 5 tasks completed"). */
	progressLabel: string;
	/** D2C has no progress UI — hide the sub-step progress circle when true. */
	hideProgress?: boolean;
	children: ReactNode;
}

/**
 * Accordion section for a single sub-step within the current journey step.
 * The progress circle renders completed-over-total task counts (not a step
 * fraction), matching the T07 design intent.
 *
 * Shares GetReadyStepSection.module.css so the visual shell is identical; the
 * only behavioural difference is the circle input.
 */
export function GetReadySubStepSection({
	subStepTitle,
	completedCount,
	totalCount,
	defaultOpen = false,
	forceOpen = false,
	expandLabel,
	collapseLabel,
	progressLabel,
	hideProgress = false,
	children,
}: GetReadySubStepSectionProps) {
	const [open, setOpen] = useState(defaultOpen);

	// Edge-triggered: whenever forceOpen flips to true, expand the section.
	useEffect(() => {
		if (forceOpen) setOpen(true);
	}, [forceOpen]);

	const bodyId = useId();

	const isEmpty = totalCount === 0;
	const effectiveOpen = isEmpty ? false : open;

	return (
		<section
			className={styles.section}
			data-step-status="in_progress"
			data-open={effectiveOpen ? "true" : "false"}
		>
			<button
				type="button"
				className={styles.header}
				aria-expanded={effectiveOpen}
				aria-controls={bodyId}
				aria-label={effectiveOpen ? collapseLabel : expandLabel}
				onClick={() => {
					if (!isEmpty) setOpen((prev) => !prev);
				}}
				disabled={isEmpty}
			>
				{!hideProgress && (
					<JourneyProgressCircle
						current={completedCount}
						total={totalCount}
						ariaLabel={progressLabel}
					/>
				)}
				<span className={styles.headerText}>
					<span className={styles.stepName}>{subStepTitle}</span>
				</span>
				<span
					className={`${styles.chevron} ${effectiveOpen ? styles.chevronOpen : ""}`}
					aria-hidden="true"
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 16 16"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
						focusable="false"
					>
						<title>{effectiveOpen ? collapseLabel : expandLabel}</title>
						<path
							d="M4 6L8 10L12 6"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</span>
			</button>
			{effectiveOpen ? (
				<div id={bodyId} className={styles.body}>
					{children}
				</div>
			) : null}
		</section>
	);
}
